import { lazy, Suspense, useCallback } from "react";
import { useRouter } from "@tanstack/react-router";
import { useSearchParams } from "@/lib/router-compat";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useExternalUrlKey } from '@/hooks/useExternalUrlKey';
import { TableSkeleton } from '@/components/ui/skeletons';
import { PageHeader } from '@/components/ui/page-header';
import { PageHeaderActionsProvider } from '@/components/ui/page-header-actions';

const TasksTable = lazy(
  () => import('@/components/tasks/tasks-table').then(m => ({ default: m.TasksTable }))
);

function TasksPage() {
  return (
    <Suspense fallback={null}>
      <TasksPageInner />
    </Suspense>
  );
}

function TasksPageInner() {
  usePageTitle('Tasks');
  const router = useRouter();
  const searchParams = useSearchParams();
  // Remount the table only when the URL changes externally (agent navigation,
  // history); self-driven filter writes keep the mounted table and its focus.
  const { key: tableKey, markSelfNavigation } = useExternalUrlKey(
    searchParams.toString(),
    searchParams,
  );

  const handleQueryParamsChange = useCallback(
    (params: {
      ownerId?: string;
      status?: string;
      priority?: string;
      q?: string;
    }) => {
      markSelfNavigation(params);
      void router.navigate({
        to: "/dashboard/tasks",
        search: params as never,
        replace: true,
      });
    },
    [router, markSelfNavigation],
  );

  return (
    <PageHeaderActionsProvider>
      <div className="flex flex-col gap-2 flex-1 min-h-0">
        <PageHeader title="Tasks" askAi />
        <TasksTable
          key={tableKey}
          initialOwnerId={searchParams.get("ownerId")}
          initialStatus={searchParams.get("status")}
          initialPriority={searchParams.get("priority")}
          initialQuery={searchParams.get("q")}
          onQueryParamsChange={handleQueryParamsChange}
        />
      </div>
    </PageHeaderActionsProvider>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/tasks')({
  component: TasksPage,
  pendingComponent: () => <div className="flex flex-col gap-4 flex-1 min-h-0"><TableSkeleton rows={8} /></div>,
  pendingMs: 0,
  pendingMinMs: 150,
});
